import React, { useEffect, useState } from "react";
import { Select, Spin } from "antd";
import { carApi } from "../../app/api/car.api";
import type {
  CarBrandDto,
  CarGenerationDto,
  CarModelDto,
} from "../../app/types/car.types";
import styles from "./CarSelectPage.module.css";

export const CarSelectPage: React.FC = () => {
  const [brands, setBrands] = useState<CarBrandDto[]>([]);
  const [models, setModels] = useState<CarModelDto[]>([]);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [modelId, setModelId] = useState<string | null>(null);
  const [generation, setGeneration] = useState<CarGenerationDto | null>(null);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);

  const uniqById = <T extends { id: string }>(list: T[]) =>
    list.filter((item, i, arr) => arr.findIndex((x) => x.id === item.id) === i);
  const generationValue = (g: CarGenerationDto) =>
    g.restylingId ? `${g.generationId}-${g.restylingId}` : g.generationId;

  useEffect(() => {
    carApi
      .getBrands()
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setBrands(uniqById(list));
      })
      .catch(() => setBrands([]))
      .finally(() => setLoadingBrands(false));
  }, []);

  useEffect(() => {
    if (!brandId) {
      setModels([]);
      setModelId(null);
      setGeneration(null);
      return;
    }
    setLoadingModels(true);
    setModelId(null);
    setGeneration(null);
    carApi
      .getModelsByBrandId(brandId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setModels(uniqById(list));
      })
      .finally(() => setLoadingModels(false));
  }, [brandId]);

  const onBrandChange = (id: string | undefined) => setBrandId(id ?? null);
  const onModelChange = (id: string | undefined) => {
    setModelId(id ?? null);
    setGeneration(null);
  };
  const selectedModel = models.find((m) => m.id === modelId);
  const generationsRaw = selectedModel?.generationDto ?? [];
  const generations = generationsRaw.filter(
    (g, i, arr) => arr.findIndex((x) => generationValue(x) === generationValue(g)) === i
  );
  const onGenerationChange = (value: string | undefined) => {
    const gen = value
      ? generations.find((g) => generationValue(g) === value)
      : null;
    setGeneration(gen ?? null);
  };

  const selectedBrand = brands.find((b) => b.id === brandId);
  const showCard = selectedBrand && selectedModel && generation;
  const imageId = generation?.images?.[0];
  const generationSelectValue = generation ? generationValue(generation) : undefined;

  return (
    <Spin spinning={loadingBrands || loadingModels} className={styles.pageSpin}>
      <div className={styles.page}>
        <div className={styles.selectors}>
        <div className={styles.block}>
          <span className={styles.label}>Марка</span>
          <Select
            placeholder="Выберите марку"
            value={brandId ?? undefined}
            onChange={onBrandChange}
            allowClear
            showSearch
            optionFilterProp="label"
            loading={loadingBrands}
            notFoundContent="Нет данных"
            className={styles.select}
            options={brands.map((b) => ({ label: b.name, value: b.id }))}
          />
        </div>
        <div className={styles.block}>
          <span className={styles.label}>Модель</span>
          <Select
            placeholder="Выберите модель"
            value={modelId ?? undefined}
            onChange={onModelChange}
            disabled={!brandId}
            allowClear
            showSearch
            optionFilterProp="label"
            loading={loadingModels}
            notFoundContent="Нет данных"
            className={styles.select}
            options={models.map((m) => ({ label: m.name, value: m.id }))}
          />
        </div>
        <div className={styles.block}>
          <span className={styles.label}>Поколение</span>
          <Select
            placeholder="Выберите поколение"
            value={generationSelectValue}
            onChange={onGenerationChange}
            disabled={!modelId || generations.length === 0}
            allowClear
            showSearch
            optionFilterProp="label"
            notFoundContent="Нет данных"
            className={styles.select}
            options={generations.map((g) => ({
              label: g.name,
              value: generationValue(g),
            }))}
          />
        </div>
      </div>
      <div className={styles.cardWrap}>
        {showCard ? (
          <div className={styles.card}>
            <div className={styles.imageBox}>
              {imageId ? (
                <img
                  src={carApi.getModelImageUrl(imageId)}
                  alt=""
                  className={styles.image}
                />
              ) : (
                <span className={styles.imagePlaceholder}>ИЗОБРАЖЕНИЕ</span>
              )}
            </div>
            <div className={styles.info}>
              <div>{selectedBrand.name}</div>
              <div>{selectedModel.name}</div>
              <div>{generation.name}</div>
              {generation.codeNames?.length ? (
                <div>Коды: {generation.codeNames.join(", ")}</div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
    </Spin>
  );
};
